/**
 * Implements software requirements: H1SR-11, H1SR-86
 *
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-11/Default-values-for-cases-can-be-defined-in-the-hip-preferences
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-86/Surgeon-users-are-able-to-define-from-a-selection-of-stem-types-which-stems-to-be-used-in-the-fitting-process
 */
import { isEmpty } from 'lodash';
import {
    isDateIsoString,
    isInRange,
    isInteger,
    isNotEmpty,
    isPositiveInteger,
} from '@/util/validation';
import { type CaseSettingsInputs } from '@/stores/caseSettings/types';
import { Side } from '@/domain/anatomy/side';
import { PatientSex } from '@/domain/anatomy/sex';
import {
    MAX_ANTEVERSION,
    MAX_INCLINATION,
    MIN_ANTEVERSION,
    MIN_INCLINATION,
} from '@/domain/anatomy/pelvis/anteversionInclination';

export enum AnteversionMode {
    Auto = 'auto',
    Manual = 'manual',
}

export type Validator<T> = (state: T) => boolean;

export type ValidatorsDefinition<T> = { [key: string]: Validator<T> };

/**
 * functions should return true when the input is valid
 * and false when it is not valid.
 */
export const validators: ValidatorsDefinition<CaseSettingsInputs> = {
    surgeryName: ({ surgery: { name } }) => {
        return isNotEmpty(name);
    },
    surgeryDate: ({ surgery: { date } }) =>
        !isNotEmpty(date) || (isNotEmpty(date) && isDateIsoString(date)),
    surgerySide: ({ surgery: { side } }) =>
        side !== '' && (side === Side.Left || side === Side.Right),
    patientFirstName: ({ patient }) => isNotEmpty(patient.firstName),
    patientFamilyName: ({ patient }) => isNotEmpty(patient.familyName),
    patientSex: ({ patient }) =>
        patient.sex !== '' &&
        (patient.sex === PatientSex.Male || patient.sex === PatientSex.Female),
    patientDateOfBirth: ({ patient }) =>
        isNotEmpty(patient.dateOfBirth) && isDateIsoString(patient.dateOfBirth),
    surgeon: ({
        specifications: {
            surgeon: { selected, error },
        },
    }) => {
        return selected !== null && isPositiveInteger(`${selected}`) && !error;
    },
    stems: ({
        specifications: {
            stems: { selected, preferredSystem },
        },
    }) => {
        // Must have at least one stem selected
        if (isEmpty(selected)) return false;
        
        // At least one stem must be from the preferred system
        const hasStemFromPreferredSystem = selected.some((stemId: string) => 
            stemId.includes(preferredSystem)
        );
        return hasStemFromPreferredSystem;
    },
    preferredSystem: ({
        specifications: {
            stems: { preferredSystem },
        },
    }) => {
        return isNotEmpty(preferredSystem);
    },
    targetLegLengthChange: ({
        specifications: {
            targets: { legLength },
        },
    }) => isInteger(legLength) && isInRange(legLength, -20, 20, true),
    targetOffsetChange: ({
        specifications: {
            targets: { offset },
        },
    }) => isInteger(offset) && isInRange(offset, -20, 20, true),
    cupInclinationAngle: ({
        specifications: {
            cup: { inclinationAngle },
        },
    }) => {
        return isInteger(inclinationAngle) && isInRange(inclinationAngle, MIN_INCLINATION, MAX_INCLINATION, true);
    },
    cupFittingMode: ({
        specifications: {
            cup: { fitMethod },
        },
    }) => isNotEmpty(fitMethod),
    cupAnteversionMode: ({
        specifications: {
            cup: { anteversion },
        },
    }) => anteversion !== undefined && anteversion !== null && anteversion.length > 0,
    cupAnteversionAngle: ({
        specifications: {
            cup: { anteversion, anteversionAngle },
        },
    }) => {
        return (
            anteversion !== undefined &&
            anteversion !== null &&
            (anteversion === AnteversionMode.Auto ||
                (anteversion === AnteversionMode.Manual &&
                    isInteger(anteversionAngle) &&
                    isInRange(anteversionAngle, MIN_ANTEVERSION, MAX_ANTEVERSION, true)))
        );
    },
    alignmentMode: ({
        specifications: {
            cup: { alignment },
        },
    }) => alignment !== undefined && alignment !== null && alignment.length > 0,
};

export function validateInputs<T>(validators: ValidatorsDefinition<T>, object: T): boolean {
    const values = Object.entries(validators).map(([key, validator]) => {
        const valid = validator(object);

        if (!valid) {
            console.warn(`${key} is not valid`);
        }

        return valid;
    });

    return values.every((result) => result);
}
